import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { cleanHandle } from "@/lib/utils/csvParsing";

type InfluencerForm = {
  id: string;
  name: string;
  handle: string;
  platform: string;
  location?: string;
  engagement_rate?: number;
  followers_count?: number;
  categories?: string[];
};

interface EditInfluencerModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  influencer: InfluencerForm;
  onSaved: () => void;
}

const categoryOptions = [
  "Fashion", "Technology", "Lifestyle", "Food", "Fitness", "Beauty",
  "Travel", "Parenting", "Finance", "Education", "Luxury", "D2C"
];

export function EditInfluencerModal({
  open,
  onOpenChange,
  influencer,
  onSaved,
}: EditInfluencerModalProps) {
  const [values, setValues] = useState<InfluencerForm>(influencer);
  const [loading, setLoading] = useState(false);
  const { currentOrganization } = useOrganization();

  React.useEffect(() => {
    setValues(influencer); // Sync form with latest influencer data when modal opens
  }, [influencer, open]);

  const handleChange = (field: keyof InfluencerForm, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCategory = (cat: string) => {
    setValues((prev) => ({
      ...prev,
      categories: prev.categories
        ? prev.categories.includes(cat)
          ? prev.categories.filter((c) => c !== cat)
          : [...prev.categories, cat]
        : [cat],
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const update: any = {
      name: values.name.trim(),
      handle: cleanHandle(values.handle),
      platform: values.platform,
      location: values.location || null,
      engagement_rate: values.engagement_rate ?? null,
      followers_count: values.followers_count ?? null,
      categories: values.categories && values.categories.length > 0 ? values.categories : null,
      updated_at: new Date().toISOString(),
    };
    try {
      const { error } = await supabase
        .from("influencers")
        .update(update)
        .eq("id", values.id);

      if (error) {
        toast({
          title: "Update failed",
          description: error.message || "Something went wrong.",
          variant: "destructive",
        });
      } else {
        toast({ title: "Influencer updated!", duration: 2500 });
        onSaved();
        onOpenChange(false);
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Show loading or error state if no organization
  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Influencer</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            <p className="text-gray-600">Please select an organization first.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Influencer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="text-sm font-medium block mb-1">Full Name</label>
            <Input
              value={values.name}
              onChange={e => handleChange("name", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Handle</label>
            <Input
              value={values.handle}
              onChange={e => handleChange("handle", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Platform</label>
            <select
              value={values.platform}
              className="w-full border rounded-md px-3 py-2"
              onChange={e => handleChange("platform", e.target.value)}
            >
              <option value="instagram">Instagram</option>
              <option value="youtube">YouTube</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Location</label>
            <Input
              value={values.location || ""}
              onChange={e => handleChange("location", e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Followers Count</label>
            <Input
              type="number"
              value={values.followers_count ?? ""}
              onChange={e => handleChange("followers_count", Number(e.target.value) || null)}
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Engagement Rate (%)</label>
            <Input
              type="number"
              value={values.engagement_rate ?? ""}
              onChange={e => handleChange("engagement_rate", Number(e.target.value) || null)}
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Categories</label>
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  className={`px-2 py-1 rounded-full text-xs border transition ${
                    values.categories?.includes(cat)
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-gray-100 border-gray-200 text-gray-700"
                  }`}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
